/**
 * useTelemetry — React hook for Application Insights integration.
 *
 * Initializes telemetry on mount (once), sets userId on auth change,
 * and tracks page views on route changes.
 *
 * Mount in AppLayout (Tier 1 — fires once for app lifetime).
 */

import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { db } from "@/db/schema"
import {
  initializeTelemetry,
  setTelemetryUserId,
  trackPageView,
} from "./client"

export function useTelemetry(): void {
  const location = useLocation()
  const hasInitialized = useRef(false)

  // ── Initialize telemetry once ──
  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    void initializeTelemetry({
      appVersion: __APP_VERSION__,
    })
  }, [])

  // ── Track userId changes ──
  useEffect(() => {
    const sub = db.cloud.currentUser.subscribe((user) => {
      if (user?.userId && user.userId !== "unauthorized") {
        setTelemetryUserId(user.userId)
      }
    })
    return () => sub.unsubscribe()
  }, [])

  // ── Track page views on route change ──
  useEffect(() => {
    trackPageView(location.pathname, location.pathname + location.search)
  }, [location.pathname, location.search])
}

// Injected by Vite at build time
declare const __APP_VERSION__: string
